import React, { useMemo, useState } from "react";
import styles from "./relatedProductsSearch.module.scss";
import { SearchOutlined } from "@mui/icons-material";
import { FormControl, InputLabel, OutlinedInput } from "@mui/material";

const RelatedProductsSearch = ({
  products = [],
  selectedProductIds = [],
  onToggle = () => {},
  onSave = () => {},
}) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = products || [];
    if (!q) return list;
    return list.filter((product) => {
      const title = getProductTitle(product).toLowerCase();
      return title.includes(q);
    });
  }, [products, query]);

  const handleToggle = (id) => {
    onToggle?.(id);
  };

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <div className={styles.searchWrapper}>
          <FormControl style={{ width: "100%" }}>
            <InputLabel htmlFor="searchProducts-outlined-input">
              Search Products
            </InputLabel>
            <OutlinedInput
              id="searchProducts-outlined-input"
              startAdornment={<SearchOutlined style={{ marginRight: "8px" }} />}
              label="Search Products"
              style={{ borderRadius: "12px" }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </FormControl>
        </div>

        <button className={styles.saveButton} onClick={() => onSave?.()}>
          Save
        </button>
      </div>

      <div className={styles.listWrapper}>
        {filtered.map((product, idx) => {
          const title = getProductTitle(product);
          const price = getProductPrice(product);
          const image = getProductImage(product);
          const isAdded = selectedProductIds.includes(product?.id);

          return (
            <div key={product?.id || idx} className={styles.itemWrapper}>
              <div className={styles.left}>
                {image ? (
                  <img className={styles.avatar} src={image} alt={title} />
                ) : (
                  <div className={styles.fallbackAvatar}>{title.charAt(0)}</div>
                )}
              </div>

              <div className={styles.middle}>
                <h4 className={styles.title}>{title}</h4>
                {price ? (
                  <div className={styles.price}>₹{price}</div>
                ) : (
                  <div className={styles.priceMuted}>Price unavailable</div>
                )}
              </div>

              <div className={styles.right}>
                {isAdded ? (
                  <button
                    className={styles.addedBadge}
                    onClick={() => handleToggle(product?.id)}
                  >
                    <span className={styles.checkIcon} aria-hidden>
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path
                          d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    Added
                  </button>
                ) : (
                  <button
                    className={styles.addButton}
                    onClick={() => handleToggle(product?.id)}
                  >
                    + Add
                  </button>
                )}
              </div>

              {idx !== filtered.length - 1 && (
                <div className={styles.divider} />
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className={styles.emptyState}>No products found.</div>
        )}
      </div>
    </div>
  );
};

const getProductTitle = (product = {}) => {
  return (
    product?.name ||
    product?.title ||
    product?.productName ||
    product?.displayName ||
    `Product ${product?.id ?? ""}`
  );
};

const getProductPrice = (product = {}) => {
  return (
    product?.price ??
    product?.sellingPrice ??
    product?.discountPrice ??
    product?.mrp ??
    product?.amount ??
    ""
  );
};

const getProductImage = (product = {}) => {
  if (product?.imageUrl) return product.imageUrl;
  if (product?.thumbnailUrl) return product.thumbnailUrl;
  if (product?.thumbnail) return product.thumbnail;
  if (product?.image) return product.image;
  if (Array.isArray(product?.images) && product.images[0]?.imageUrl)
    return product.images[0].imageUrl;
  if (Array.isArray(product?.productImages) && product.productImages[0]?.url)
    return product.productImages[0].url;
  return "";
};

export default RelatedProductsSearch;
